import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { seedSelectedFeaturedPeople } from '../api/get-featured-people';
import { TFeaturedPeopleType } from '../types';

import { usePeoplePicker } from './use-people-picker';

type PeopleFormFields = { people_id: string | null; entity_label?: string };

type UsePeopleFormPickerParams<TForm extends PeopleFormFields> = {
  form: UseFormReturn<TForm>;
  /** The label stored on the presence or declaration being edited. */
  fallbackLabel?: string | null;
  /**
   * Keeps `entity_label` in step with the chosen person while the form is the one on screen.
   * Declarations leave it off: there the label is the free-text declarant.
   */
  syncLabel: boolean;
};

/**
 * The person picker of a presence or declaration form: names the chosen person, keeps the label
 * in step with it, and selects a person created from the picker.
 */
export function usePeopleFormPicker<TForm extends PeopleFormFields>({
  form,
  fallbackLabel,
  syncLabel,
}: UsePeopleFormPickerParams<TForm>) {
  const queryClient = useQueryClient();
  // The generic form only guarantees the two fields this hook reads and writes.
  const fields = form as unknown as UseFormReturn<PeopleFormFields>;
  const peopleId = fields.watch('people_id');
  const picker = usePeoplePicker({ value: peopleId, fallbackLabel });

  const selectedPerson = picker.findOption(peopleId)?.entity ?? null;
  const selectedName = selectedPerson?.name ?? '';

  // RISK: written only once the value is settled. While a new presence's person is being named,
  // writing would replace its label with an empty one.
  useEffect(() => {
    if (!syncLabel || !peopleId || picker.isValueResolving) return;
    fields.setValue('entity_label', selectedName);
  }, [syncLabel, peopleId, picker.isValueResolving, selectedName, fields]);

  const selectCreatedPerson = useCallback(
    (person: TFeaturedPeopleType) => {
      seedSelectedFeaturedPeople(queryClient, person);
      fields.setValue('people_id', person.id, { shouldValidate: true });
    },
    [queryClient, fields]
  );

  return { picker, selectedPerson, selectCreatedPerson };
}
